import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Edit3, MessageSquare, Sparkles, Save, Send, Loader, Clock, Zap, Users, Calendar, DollarSign, CalendarPlus, Phone, MapPin, Building, AlertCircle, CheckCircle, ChevronDown, Lock, Trash2 } from 'lucide-react';
import { arrayUnion } from 'firebase/firestore';
import { STAGES, MANAGERS, VENUES, EVENT_TYPES, LOSS_REASONS } from '../../lib/constants';
import { getActivityMeta, formatActivityMessage, createActivityEntry, ACTIVITY_TYPES, formatDate } from '../../utils/helpers';
import {
  generateSiteVisitCalendarUrl,
  generateCallCalendarUrl,
  generateEventCalendarUrl,
  openInGoogleCalendar
} from '../../utils/googleCalendar';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { leadsService, contactService } from '../../services/api';
import { useSocket } from '../../hooks/useSocket';
import { bookingService } from '../../services/booking.service';
import { useLanguage } from '../../contexts/LanguageContext';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker, CustomCheckbox, Toast } from '../ui';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function LeadDetailModal({
  lead,
  currentUser,
  onClose,
  onSave,
  onOpenAi,
  sources,
  managers,
  eventTypes,
  venues,
  onDelete,
  onRefreshLeads
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState(null);
  // Strip internal UI flags before initializing formData — prevents them from
  // leaking into onSave() calls and being persisted to the server/DB.
  const { _pendingWon: _stripPendingWon, _pendingSiteVisited: _stripPendingSiteVisited, ...cleanLeadData } = lead;
  
  const [formData, setFormData] = useState(() => {
    const data = { ...cleanLeadData };
    // siteVisitTime is not a separate DB column, so we extract it from the siteVisitDate
    if (data.siteVisitDate) {
      try {
        const d = new Date(data.siteVisitDate);
        if (!isNaN(d.getTime())) {
          // Format as HH:mm in local time
          data.siteVisitTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        }
      } catch (e) {}
    }
    return data;
  });
  
  const [comment, setComment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stageChangeConfirm, setStageChangeConfirm] = useState(null); // { from, to }
  const [stageChangeNote, setStageChangeNote] = useState('');
  // Loss reason — collected in the same confirm dialog when the target stage
  // is Lost, so it costs one extra click and can never be skipped.
  const [lossReason, setLossReason] = useState('');
  // What we told the customer. Required to enter Quoted — see handleStageClick.
  const [quotedAmount, setQuotedAmount] = useState('');

  // ── Contact editing ──────────────────────────────────────────────────────
  // A DM has no phone number, so the thread's sender id stands in as the
  // contact's identifier (`ig:997538…`). The customer sends their real number
  // into the thread minutes later, and until it reaches the contact record the
  // CRM cannot recognise them: their first WhatsApp reply arrives from an
  // unknown number and becomes a second contact, a second lead and a second
  // owner. The number is in the room the whole time — there was just nowhere
  // on this screen to put it.
  const isPlaceholderPhone = (p) => /^(ig|fb|msg|meta-|unknown-)/i.test(String(p || ''));
  // Mirrors phoneProblem() on the server so the rep is told while they are
  // still looking at the number, not after a round trip. Deliberately narrow:
  // production holds landlines and years of imported oddities, and refusing
  // those would block edits to contacts that are otherwise fine. It catches
  // the one failure that matters — a mobile carrying the wrong digit count,
  // which is what "+92" typed in front of an 03… number produces. Seven
  // contacts are already in that state, silently matching nothing.
  const phoneProblem = (raw) => {
    if (!raw) return null;
    let d = String(raw).replace(/\D/g, '');
    if (!d) return 'That does not look like a phone number.';
    if (d.startsWith('0092') && d.length >= 14) d = d.slice(2);
    else if (d.startsWith('03') && d.length === 11) d = '92' + d.slice(1);
    else if (d.startsWith('3') && d.length === 10) d = '92' + d;
    else if (d.startsWith('920')) d = '92' + d.slice(3);
    const wrongLength = (n) => (t('phoneWrongLength')
      || 'A mobile number should be 11 digits (03XX XXXXXXX). That one has {n}.').replace('{n}', n);
    if (d.startsWith('923') && d.length !== 12) return wrongLength(d.length - 1);
    // A correct 03XXXXXXXXX became 923… above, so anything still starting 03 is
    // a mobile with the wrong digit count. Landlines (021, 041…) are untouched.
    if (d.startsWith('03')) return wrongLength(d.length);
    if (d.length < 9) return t('phoneTooShort') || 'That number is too short.';
    return null;
  };
  const [contactDraft, setContactDraft] = useState(null);
  // What the draft is measured against. Held locally rather than read from the
  // lead prop so "saved" doesn't depend on a list refetch landing: the write
  // succeeded, and the card should say so whether or not the refresh is slow,
  // cached or failed.
  const [contactBaseline, setContactBaseline] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [contactConflict, setContactConflict] = useState(null);
  const [contactError, setContactError] = useState('');
  useEffect(() => {
    const c = lead?.contact;
    if (!c) { setContactDraft(null); setContactBaseline(null); return; }
    const seed = {
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: isPlaceholderPhone(c.phone) ? '' : (c.phone || '')
    };
    setContactDraft(seed);
    setContactBaseline(seed);
    setContactConflict(null);
    setContactError('');
    setContactSaved(false);
    // Keyed on the contact, not its fields: re-seeding whenever a value changes
    // would wipe the rep's half-typed number the moment a refresh lands.
  }, [lead?.contact?.id]);

  // ── Re-sync contact form when BMS updates name/phone on the SAME contact ──
  // contact.id stays the same, so the effect above won't fire — watch the fields.
  useEffect(() => {
    const c = lead?.contact;
    if (!c) return;
    // Only overwrite if the user has NOT started editing (draft matches baseline)
    const userIsEditing = contactDraft && contactBaseline && (
      contactDraft.firstName !== contactBaseline.firstName ||
      contactDraft.lastName  !== contactBaseline.lastName  ||
      contactDraft.phone     !== contactBaseline.phone
    );
    if (userIsEditing) return; // don't wipe their in-progress edits
    const seed = {
      firstName: c.firstName || '',
      lastName:  c.lastName  || '',
      phone: isPlaceholderPhone(c.phone) ? '' : (c.phone || '')
    };
    setContactDraft(seed);
    setContactBaseline(seed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.contact?.firstName, lead?.contact?.lastName, lead?.contact?.phone]);

  const contactDirty = !!contactDraft && !!contactBaseline && (
    contactDraft.firstName !== contactBaseline.firstName
    || contactDraft.lastName !== contactBaseline.lastName
    || contactDraft.phone !== contactBaseline.phone
  );

  const handleSaveContact = async () => {
    if (!lead?.contact?.id || !contactDirty) return;
    setContactSaving(true);
    setContactConflict(null);
    setContactError('');
    // Only changed fields travel: the leads list carries a partial contact
    // (no email), so sending the whole draft would blank what it never loaded.
    const payload = {};
    if (contactDraft.firstName !== contactBaseline.firstName) payload.firstName = contactDraft.firstName.trim();
    if (contactDraft.lastName !== contactBaseline.lastName) payload.lastName = contactDraft.lastName.trim();
    if (contactDraft.phone && contactDraft.phone !== contactBaseline.phone) payload.phone = contactDraft.phone.trim();
    try {
      await contactService.update(lead.contact.id, payload);
      setContactBaseline({ ...contactDraft });
      setContactSaved(true);
      // The lead list holds its own copy of the contact; refresh so the header,
      // the leads table and the next open of this modal all agree.
      if (onRefreshLeads) await onRefreshLeads();
    } catch (err) {
      // 409 carries the contact that already owns the number — that is the
      // answer the rep needs, not an error to dismiss.
      const c = err?.payload?.conflict;
      if (c) setContactConflict(c);
      else setContactError(err?.message || 'Could not save the contact');
    } finally {
      setContactSaving(false);
    }
  };
  const [bookingConfirm, setBookingConfirm] = useState(null); // For booking confirmation modal
  // Tracks the source of a pending gated transition.
  // source: 'kanban' → auto-close LeadDetailModal on cancel
  // source: 'stagePill' → only close booking modal on cancel
  // Object-based so future gated stages (e.g. Lost) can share the same pattern.
  const [pendingTransition, setPendingTransition] = useState(
    lead._pendingWon ? { source: 'kanban', from: lead.stage, to: 'Won' } : null
  );
  const [bookingData, setBookingData] = useState({
    venue: '',
    eventDate: '',
    eventType: '',
    guests: '',
    finalAmount: '',
    advanceAmount: '',
    shift: '',
    notes: '',
    clientCnic: '',
    clientAddress: '',
    clientEmail: ''
  });
  const [venueCheckStatus, setVenueCheckStatus] = useState(null); // null | 'checking' | 'available' | 'booked'
  const [venueBookedInfo, setVenueBookedInfo] = useState(null); // { clientName, orderNo, shift }
  const [isStageChanging, setIsStageChanging] = useState(false);
  const [calendarDeleteConfirm, setCalendarDeleteConfirm] = useState(null); // null | 'siteVisit' | 'call' | 'event'

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }, []);

  const isPastEventDate = false;

  // --- Quotation Items State ---
  const [quotationItems, setQuotationItems] = useState(() => {
    const s = lead.quotationItems || {};
    return {
      lawnRent: { lumpsum: s.lawnRent?.lumpsum || '', discount: s.lawnRent?.discount || '' },
      decoration: { lumpsum: s.decoration?.lumpsum || '', discount: s.decoration?.discount || '' },
      coldDrink: { enabled: s.coldDrink?.enabled || 'no', rate: s.coldDrink?.rate || '', discount: s.coldDrink?.discount || '', inputMode: s.coldDrink?.inputMode || 2, lumpsum: s.coldDrink?.lumpsum || '' },
      mineralWater: { enabled: s.mineralWater?.enabled || 'no', rate: s.mineralWater?.rate || '', discount: s.mineralWater?.discount || '', inputMode: s.mineralWater?.inputMode || 2, lumpsum: s.mineralWater?.lumpsum || '' },
      pantryCharges: { enabled: s.pantryCharges?.enabled || 'no', rate: s.pantryCharges?.rate || '', discount: s.pantryCharges?.discount || '' },
      freshFlower: { enabled: s.freshFlower?.enabled || 'no', rate: s.freshFlower?.rate || '', discount: s.freshFlower?.discount || '' },
      addons: s.addons || []
    };
  });

  const activityEndRef = useRef(null);
  const { syncLeadEvent, shouldAutoSync } = useGoogleCalendar();
  const [timelineActivities, setTimelineActivities] = useState([]);

  const [timelineLoading, setTimelineLoading] = useState(true);

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const activities = await leadsService.getTimeline(lead.id);
      setTimelineActivities(activities);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };


  useEffect(() => {
    if (lead.id) {
      fetchTimeline();
    }

    const handleTimelineUpdate = (e) => {
      if (e.detail?.leadId === lead.id) {
        fetchTimeline();
      }
    };

    window.addEventListener('lead_timeline_updated', handleTimelineUpdate);
    return () => window.removeEventListener('lead_timeline_updated', handleTimelineUpdate);
  }, [lead.id]);

  // ── Sync form data and quotation items whenever lead prop is updated (e.g. from BMS webhook sync) ──
  const syncLeadToLocalState = (leadData) => {
    if (!leadData || !leadData.id) return;
    setFormData(prev => ({
      ...prev,
      stage: leadData.stage || leadData.status || prev.stage,
      status: leadData.status || leadData.stage || prev.status,
      clientName: leadData.clientName || prev.clientName,
      phone: leadData.phone || prev.phone,
      email: leadData.email || prev.email,
      clientBudget: leadData.clientBudget || '',
      quotationAmount: leadData.quotationAmount || leadData.finalAmount || prev.quotationAmount,
      guests: leadData.guests || prev.guests,
      eventDate: (function () {
        if (!leadData.eventDate) return '';
        try { return new Date(leadData.eventDate).toISOString().split('T')[0]; }
        catch (e) { return leadData.eventDate; }
      })(),
      shift: leadData.shift || prev.shift,
      eventType: leadData.eventType || prev.eventType,
      venue: leadData.venue || prev.venue,
      manager: leadData.manager || prev.manager,
      source: leadData.source || prev.source,
      notes: leadData.notes || prev.notes,
      siteVisited: leadData.siteVisited || prev.siteVisited
    }));

    if (leadData.quotationItems && typeof leadData.quotationItems === 'object') {
      const s = leadData.quotationItems;
      setQuotationItems({
        lawnRent: { lumpsum: s.lawnRent?.lumpsum || '', discount: s.lawnRent?.discount || '' },
        decoration: { lumpsum: s.decoration?.lumpsum || '', discount: s.decoration?.discount || '' },
        coldDrink: { enabled: s.coldDrink?.enabled || 'no', rate: s.coldDrink?.rate || '', discount: s.coldDrink?.discount || '', inputMode: s.coldDrink?.inputMode || 2, lumpsum: s.coldDrink?.lumpsum || '' },
        mineralWater: { enabled: s.mineralWater?.enabled || 'no', rate: s.mineralWater?.rate || '', discount: s.mineralWater?.discount || '', inputMode: s.mineralWater?.inputMode || 2, lumpsum: s.mineralWater?.lumpsum || '' },
        pantryCharges: { enabled: s.pantryCharges?.enabled || 'no', rate: s.pantryCharges?.rate || '', discount: s.pantryCharges?.discount || '' },
        freshFlower: { enabled: s.freshFlower?.enabled || 'no', rate: s.freshFlower?.rate || '', discount: s.freshFlower?.discount || '' },
        addons: s.addons || []
      });
    }
  };

  useEffect(() => {
    syncLeadToLocalState(lead);
  }, [lead]);

  // ── Listen for real-time BMS webhook updates via socket ──
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket || !lead?.id) return;

    const handleLeadUpdated = async (payload) => {
      // Only react if this event is for the currently open lead
      if (payload?.leadId !== lead.id) return;

      console.log('🔄 [LeadDetailModal] Real-time lead update received from', payload.source || 'unknown', '— re-fetching lead data...');

      try {
        // Re-fetch the full lead from the API to get all updated fields including quotationItems
        const freshLead = await leadsService.getById(lead.id);
        if (freshLead) {
          // Build a lead object matching the shape the modal expects
          const updatedLead = {
            ...freshLead,
            clientName: freshLead.contact ? [freshLead.contact.firstName, freshLead.contact.lastName].filter(Boolean).join(' ') : freshLead.title || lead.clientName,
            stage: freshLead.status,
            phone: freshLead.contact?.phone || lead.phone,
            email: freshLead.contact?.email || lead.email,
            manager: freshLead.assignee?.username || lead.manager,
            source: freshLead.source?.name || lead.source
          };
          syncLeadToLocalState(updatedLead);
          // Also refresh the timeline to show the new BMS_SYNC entry
          fetchTimeline();
        }
      } catch (err) {
        console.warn('[LeadDetailModal] Failed to re-fetch lead after socket update:', err.message);
      }
    };

    socket.on('lead_updated', handleLeadUpdated);
    return () => socket.off('lead_updated', handleLeadUpdated);
  }, [socket, lead?.id]);

  // ── Auto-trigger booking modal when opened via Kanban drag-to-Won ──────────
  // The Kanban board sets _pendingWon=true when a lead is dragged to Won column.
  // We detect this on mount and immediately open the Confirm Booking modal.
  useEffect(() => {
    const alreadyTerminal = ['Won', 'Completed'].includes(lead.stage) || ['Won', 'Completed'].includes(lead.status);
    if ((lead._pendingWon || lead._pendingSiteVisited) && !alreadyTerminal) {
      const timer = setTimeout(() => {
        setBookingData({
          venue: lead.venue || '',
          eventDate: (() => {
            if (!lead.eventDate) return '';
            try { return new Date(lead.eventDate).toISOString().split('T')[0]; }
            catch (e) { return lead.eventDate; }
          })(),
          eventType: lead.eventType || '',
          guests: lead.guests || '',
          finalAmount: lead.quotationAmount || '',
          advanceAmount: lead.advancePaid || '',
          shift: lead.shift || '',
          notes: lead.bookingNotes || '',
          clientCnic: lead.clientCnic || '',
          clientAddress: lead.clientAddress || '',
          clientEmail: lead.email || ''
        });
        setBookingConfirm({ from: lead.stage, to: lead._pendingWon ? 'Won' : 'Site Visited' });
        const preVenue = lead.venue || '';
        const preDate = lead.eventDate ? new Date(lead.eventDate).toISOString().split('T')[0] : '';
        const preShift = '';
        if (preVenue && preDate) {
          setTimeout(() => checkVenueAvailability(preVenue, preDate, preShift), 50);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-trigger generic stage change confirm when opened via Kanban ────────
  useEffect(() => {
    const alreadyTerminal = ['Won', 'Completed'].includes(lead.stage) || ['Won', 'Completed'].includes(lead.status);
    if (lead._pendingStageChange && !alreadyTerminal) {
      const timer = setTimeout(() => {
        handleStageClick(lead._pendingStageChange);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── ESC key closes booking modal (P3) ────────────────────────────────────────
  // Handles keyboard dismiss on desktop. On Kanban flow this also auto-closes
  // the LeadDetailModal, consistent with Cancel/X/backdrop behaviour.
  useEffect(() => {
    if (!bookingConfirm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleBookingCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingConfirm]);


  // Combine activity log and comments for timeline
  const timeline = useMemo(() => {
    const items = [];

    // Add API activities
    timelineActivities.forEach((a) => {
      let content = {};
      try {
        content = JSON.parse(a.content);
      } catch (e) {
        content = { note: a.content };
      }

      const isBmsSync = ['BMS_SYNC', 'bms_sync'].includes(a.type);
      items.push({
        type: a.type?.toLowerCase() || 'note_added',
        data: content,
        timestamp: new Date(a.createdAt),
        user: a.user?.username || (isBmsSync ? 'Banquet System (BMS)' : a.type === 'SYSTEM' ? 'System' : 'Unknown')
      });
    });

    return items.sort((a, b) => a.timestamp - b.timestamp);
  }, [timelineActivities]);

  // --- Quotation Calculations ---
  const quotationCalc = useMemo(() => {
    const g = Number(bookingConfirm ? bookingData.guests : formData.guests) || 0;
    const lump = (l, d) => Math.max(0, (Number(l) || 0) - (Number(d) || 0));
    const perPax = (r, q, d) => Math.max(0, (Number(r) || 0) * q - (Number(d) || 0));
    const lrSub = lump(quotationItems.lawnRent.lumpsum, quotationItems.lawnRent.discount);
    const decSub = lump(quotationItems.decoration.lumpsum, quotationItems.decoration.discount);
    // Cold Drink: lumpsum mode → use lumpsum directly; perPax mode → rate × guests
    const cdRawSub = quotationItems.coldDrink.enabled === 'yes'
      ? (quotationItems.coldDrink.inputMode === 1
        ? (Number(quotationItems.coldDrink.lumpsum) || 0)
        : (Number(quotationItems.coldDrink.rate) || 0) * g)
      : 0;
    const cdSub = Math.max(0, cdRawSub - (Number(quotationItems.coldDrink.discount) || 0));
    // Mineral Water: lumpsum mode → use lumpsum directly; perPax mode → rate × guests
    const mwRawSub = quotationItems.mineralWater.enabled === 'yes'
      ? (quotationItems.mineralWater.inputMode === 1
        ? (Number(quotationItems.mineralWater.lumpsum) || 0)
        : (Number(quotationItems.mineralWater.rate) || 0) * g)
      : 0;
    const mwSub = Math.max(0, mwRawSub - (Number(quotationItems.mineralWater.discount) || 0));
    // Derived per-person rates for display
    const cdPerPax = g > 0 && quotationItems.coldDrink.enabled === 'yes' && quotationItems.coldDrink.inputMode === 1
      ? (cdSub / g)
      : null;
    const mwPerPax = g > 0 && quotationItems.mineralWater.enabled === 'yes' && quotationItems.mineralWater.inputMode === 1
      ? (mwSub / g)
      : null;
    const pcSub = quotationItems.pantryCharges.enabled === 'yes' ? lump(quotationItems.pantryCharges.rate, quotationItems.pantryCharges.discount) : 0;
    const ffSub = quotationItems.freshFlower.enabled === 'yes' ? lump(quotationItems.freshFlower.rate, quotationItems.freshFlower.discount) : 0;
    const addonsSub = quotationItems.addons ? quotationItems.addons.reduce((sum, item) => sum + Math.max(0, (Number(item.lumpsum) || 0) - (Number(item.discount) || 0)), 0) : 0;
    const total = lrSub + decSub + cdSub + mwSub + pcSub + ffSub + addonsSub;
    return { lrSub, decSub, cdSub, mwSub, pcSub, ffSub, addonsSub, total, g, cdPerPax, mwPerPax };
  }, [quotationItems, formData.guests, bookingConfirm, bookingData.guests]);

  useEffect(() => {
    if (activeTab === 'activity')
      activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab, timeline]);

  // Helper to compute what changed
  const getChangedFields = () => {
    const changedFields = [];
    const fieldsToCheck = [
      { key: 'clientName', label: 'Client Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'clientBudget', label: 'Client Budget' },
      { key: 'quotationAmount', label: 'Quotation Amount' },
      { key: 'guests', label: 'Guests' },
      { key: 'eventDate', label: 'Event Date' },
      { key: 'shift', label: 'Shift' },
      { key: 'eventType', label: 'Event Type' },
      { key: 'venue', label: 'Venue Preference' },
      { key: 'manager', label: 'Manager' },
      { key: 'source', label: 'Source' },
      { key: 'notes', label: 'Notes' },
      { key: 'siteVisitDate', label: 'Site Visit Date' },
      { key: 'siteVisitTime', label: 'Site Visit Time' },
      { key: 'siteVisited', label: 'Site Visited' }
    ];

    fieldsToCheck.forEach(field => {
      let originalValue = lead[field.key] || '';
      let newValue = formData[field.key] || '';

      // Normalize dates to YYYY-MM-DD for comparison
      if (field.key === 'eventDate' || field.key === 'siteVisitDate') {
        if (originalValue) originalValue = new Date(originalValue).toISOString().split('T')[0];
        if (newValue) newValue = new Date(newValue).toISOString().split('T')[0];
      }

      if (String(originalValue) !== String(newValue)) {
        changedFields.push(field.label);
      }
    });

    return changedFields;
  };

  // ── Calendar entry delete handler ──────────────────────────────────────────
  const calendarDeleteLabels = {
    siteVisit: { label: t('siteVisit') || 'Site Visit', date: formData.siteVisitDate },
    call: { label: t('scheduledCall') || 'Scheduled Call', date: formData.nextCallDate },
    event: { label: t('eventDate') || 'Event Date', date: formData.eventDate },
  };

  const handleCalendarEntryDelete = async () => {
    if (!calendarDeleteConfirm) return;
    
    let updatedData = { ...formData };
    switch (calendarDeleteConfirm) {
      case 'siteVisit':
        updatedData.siteVisitDate = null;
        updatedData.siteVisitTime = null;
        break;
      case 'call':
        updatedData.nextCallDate = null;
        updatedData.nextCallTime = null;
        break;
      case 'event':
        updatedData.eventDate = null;
        break;
      default:
        break;
    }
    
    setFormData(updatedData);
    setCalendarDeleteConfirm(null);
    
    // Auto-save so the call list updates immediately
    setIsSaving(true);
    try {
      await onSave(lead.id, { 
        ...updatedData, 
        quotationItems, 
        quotationAmount: quotationCalc.total || updatedData.quotationAmount 
      });
    } catch (error) {
      console.error("Failed to delete calendar entry:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Block saving if lead is locked
    if (isLockedStatus) {
      console.warn('Save blocked: lead is in a terminal stage.');
      return;
    }
    setIsSaving(true);

    // Track explicit changes
    const changedFields = getChangedFields();

    if (changedFields.length > 0) {
      try {
        await leadsService.addNote(lead.id, {
          content: JSON.stringify({ field: changedFields.join(', ') }),
          type: ACTIVITY_TYPES.DETAILS_UPDATED,
          userId: currentUser?.id
        });
      } catch (error) {
        console.error('Failed to log detail update:', error);
      }
    }

    let dataToSave = { 
      ...formData, 
      quotationItems, 
      quotationAmount: quotationCalc.total || formData.quotationAmount 
    };
        
    // Merge siteVisitTime into siteVisitDate before saving
    if (dataToSave.siteVisitDate) {
      const dateStr = String(dataToSave.siteVisitDate).split('T')[0];
      const timeStr = dataToSave.siteVisitTime || '12:00';
      dataToSave.siteVisitDate = new Date(`${dateStr}T${timeStr}:00`).toISOString();
    }

    // Sanitize date fields: empty string '' causes Joi validation error on server
    const dateFieldsToClean = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate'];
    dateFieldsToClean.forEach(field => {
      if (dataToSave[field] === '' || dataToSave[field] === undefined) {
        dataToSave[field] = null;
      }
    });

    // Save the lead with all formData + quotation items
    await onSave(lead.id, dataToSave);

    // Auto-sync calendar events if enabled
    try {
      // Check for site visit changes
      if (formData.siteVisitDate !== lead.siteVisitDate || formData.siteVisitTime !== lead.siteVisitTime) {
        if (shouldAutoSync('siteVisit')) {
          await syncLeadEvent({ ...formData, id: lead.id }, 'siteVisit', onSave);
        }
      }

      // Check for event date changes
      if (formData.eventDate !== lead.eventDate) {
        if (formData.stage === 'Won' && shouldAutoSync('booking')) {
          await syncLeadEvent({ ...formData, id: lead.id }, 'booking', onSave);
        } else if (shouldAutoSync('event')) {
          await syncLeadEvent({ ...formData, id: lead.id }, 'event', onSave);
        }
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
    }

    setIsSaving(false);
    onClose();
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    try {
      // Create activity entry for the note
      const activityData = { note: comment };

      await leadsService.addNote(lead.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.NOTE_ADDED,
        userId: currentUser.id
      });

      setComment('');
      fetchTimeline(); // Refresh timeline
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleStageClick = (newStage) => {
    if (newStage === formData.stage) return; // Already on this stage

    // Block stage changes if lead is locked (Won/Lost/Completed)
    if (isLockedStatus) {
      console.warn('Stage change blocked: lead is in a terminal stage.');
      return;
    }

    // If moving to Won or Site Visited, show booking confirmation modal
    if (newStage === 'Won' || newStage === 'Site Visited') {
      setBookingData({
        venue: formData.venue || '',
        eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : '',
        eventType: formData.eventType || '',
        guests: formData.guests || '',
        finalAmount: quotationCalc.total || formData.quotationAmount || '',
        advanceAmount: formData.advancePaid || '',
        shift: formData.shift || '',
        notes: formData.bookingNotes || '',
        clientCnic: formData.clientCnic || '',
        clientAddress: formData.clientAddress || '',
        clientEmail: formData.email || ''
      });
      setBookingConfirm({ from: formData.stage, to: newStage });
      // Auto-check availability when modal opens with pre-filled data
      const preVenue = formData.venue || '';
      const preDate = formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : '';
      const preShift = '';
      if (preVenue && preDate) {
        setTimeout(() => checkVenueAvailability(preVenue, preDate, preShift), 0);
      }
    } else {
      // Quoted has to mean a number was given to the customer. Until now it
      // meant a rep clicked a button: Won demands a final amount and Site
      // Visited a venue and date, but Quoted demanded nothing, so leads sat at
      // that stage with PKR 0 — chased by quote follow-up reminders, counted
      // in the funnel, contributing nothing to pipeline. Seed the field from
      // the builder when it has been used, so the common case is one glance
      // and Confirm; a verbal quote over WhatsApp is typed in here instead.
      if (newStage === 'Quoted') {
        setQuotedAmount(String(quotationCalc.total || formData.quotationAmount || ''));
      }
      setStageChangeConfirm({ from: formData.stage, to: newStage });
    }
  };

  const handleStageChangeConfirm = async () => {
    if (!stageChangeConfirm) return;

    setIsStageChanging(true);
    const { from, to } = stageChangeConfirm;

    // Determine what else changed while confirming stage change
    const changedFields = getChangedFields();

    // Create activity entry for stage change (includes any edited fields as part of STAGE_CHANGE activity)
    const isLoss = to === 'Lost';
    const lossLabel = isLoss
      ? (LOSS_REASONS.find((r) => r.id === lossReason)?.label || lossReason)
      : null;

    const activityData = {
      from,
      to,
      note: stageChangeNote,
      // The reason rides the timeline entry as well as the lead field: a lead
      // can be revived and re-lost for a different reason, and both readings
      // are worth keeping.
      lossReason: lossLabel,
      editedFields: changedFields.length > 0 ? changedFields.join(', ') : null
    };

    const prevLoss = { lostReason: formData.lostReason ?? null, lostReasonNote: formData.lostReasonNote ?? null };

    try {
      await isActiveStageChange(to);

      // Every active stage sets a clock. The old list here named three stages
      // that no longer exist ('Follow Up', 'Warm', 'Hot'), so in practice only
      // Site Visited ever scheduled anything — a lead moved to Quoted got no
      // next-call date from anywhere, fell out of the Call List, and sat
      // invisible until someone stumbled on it (25 active leads were in that
      // state when this was written, 22 of them Quoted or later). Cadence
      // matches the call-outcome defaults: Contacted tomorrow, Quoted and
      // Site Visited follow-up rhythm.
      const STAGE_CLOCK = {
        'Contacted': { days: 1, time: '10:00' },
        'Quoted': { days: 2, time: '11:00' },
        'Site Visited': { days: 1, time: '10:00' },
      };
      let nextCallUpdate = {};
      const clock = STAGE_CLOCK[to];
      // A rep's deliberately scheduled future call outranks the default — the
      // old code overwrote it. Auto-set only fills a blank or replaces a date
      // that has already passed (an expired clock is the same as no clock).
      const existingCall = String(formData.nextCallDate || lead.nextCallDate || '').split('T')[0];
      const todayStr = new Date().toLocaleDateString('en-CA');
      if (clock && (!existingCall || existingCall < todayStr)) {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + clock.days);
        nextCallUpdate = {
          nextCallDate: nextDate.toLocaleDateString('en-CA'),
          nextCallTime: clock.time
        };
      }

      // Merge current formData edits with stage update so all changes are saved together
      const updateData = {
        ...formData,           // include any pending field edits (quotation amount, etc.)
        stage: to,
        stageUpdatedAt: new Date().toISOString(),
        stageUpdatedBy: currentUser.name,
        quotationItems,
        // On the way into Quoted the confirmed figure wins: it is either the
        // builder's total unchanged, or the number the rep typed over it.
        quotationAmount: to === 'Quoted'
          ? (Number(quotedAmount) || quotationCalc.total || formData.quotationAmount)
          : (quotationCalc.total || formData.quotationAmount),
        ...(isLoss ? { lostReason: lossReason, lostReasonNote: stageChangeNote || null } : {}),
        ...nextCallUpdate
      };

      // Sanitize date fields: empty string '' causes Joi validation error on server
      const dateFieldsToClean = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate'];
      dateFieldsToClean.forEach(field => {
        if (updateData[field] === '' || updateData[field] === undefined) {
          updateData[field] = null;
        }
      });

      // Optimistic UI update. The loss reason must land in formData too: the
      // Save button sends the whole of formData, and a formData still holding
      // the lostReason:null it was opened with overwrote the reason a second
      // after it was saved — 361 of 363 losses in 15–28 Sep 2026 lost their
      // reason that way. Leaving Lost clears it, as the server does.
      setFormData(prev => ({
        ...prev,
        stage: to,
        lostReason: isLoss ? lossReason : null,
        lostReasonNote: isLoss ? (stageChangeNote || null) : null,
        ...(nextCallUpdate || {})
      }));
      setStageChangeConfirm(null);
      setStageChangeNote('');
      setLossReason('');
      setQuotedAmount('');

      // Save stage change + all pending field edits in ONE request
      await onSave(lead.id, updateData);

      // Auto-sync calendar event if enabled and we scheduled a call
      if (nextCallUpdate.nextCallDate && shouldAutoSync('call')) {
        try {
          await syncLeadEvent({ ...lead, ...updateData }, 'call', onSave);
        } catch (calendarError) {
          console.error('Calendar sync error during stage change:', calendarError);
        }
      }

      // Log ONE combined activity entry for the stage change (which includes edited fields info)
      await leadsService.addNote(lead.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE,
        userId: currentUser.id
      });

      fetchTimeline();
    } catch (error) {
      console.error("Error changing stage:", error);
      // Revert optimistic update on failure
      setFormData(prev => ({
        ...prev,
        stage: from,
        ...prevLoss
      }));
    } finally {
      setIsStageChanging(false);
    }
  };

  const isActiveStageChange = async (to) => {
    // Placeholder if any validation needed
    return true;
  };

  const handleStageChangeCancel = () => {
    setStageChangeConfirm(null);
    setStageChangeNote('');
    setLossReason('');
    setQuotedAmount('');
  };
  const handleBookingConfirm = async () => {
    if (!bookingConfirm) return;

    const isTentative = bookingConfirm.to === 'Site Visited';
    const finalAmt = quotationCalc.total || bookingData.finalAmount;

    // Validate required fields
    if (!bookingData.venue || !bookingData.eventDate) {
      return; // Venue and Event Date are required for both
    }
    
    // Strict validation for 'Won' stage
    if (!isTentative) {
      if (!finalAmt || !bookingData.clientCnic || !bookingData.clientAddress) {
        return;
      }
    }

    setIsStageChanging(true);
    const { from, to } = bookingConfirm;

    // Capture any pending formData field edits made before clicking Won
    const pendingChangedFields = getChangedFields();

    try {
      // ── Submit to external banquet system FIRST ──
      try {
        const g = parseInt(bookingData.guests) || 0;

        // venue_id: use numeric id if available on venue object, else try to parse
        const venueObj = (venues && venues.length > 0 ? venues : []).find(v => v.id === bookingData.venue);
        const venueNumericId = venueObj?.venue_id ?? venueObj?.numericId ?? venueObj?.venueId
          ?? (Number.isInteger(Number(bookingData.venue)) ? Number(bookingData.venue) : null);

        // shift_id: Morning=20, Evening=19
        const shiftId = bookingData.shift === 'Evening' ? 19 : 20;

        // event_type_id: find from eventTypes list (supports both string[] and object[] with event_type_id)
        const etList = eventTypes && eventTypes.length > 0 ? eventTypes : [];
        const etObj = etList.find(et => {
          const n = typeof et === 'string' ? et : et.name;
          return n === bookingData.eventType || et?.id === bookingData.eventType;
        });
        const eventTypeId = etObj
          ? (typeof etObj === 'object' && etObj.event_type_id ? etObj.event_type_id : etList.findIndex(et => (typeof et === 'string' ? et : et.name) === bookingData.eventType) + 1)
          : null;

        // Helper: is service enabled?
        const isEnabled = (val) => val === 'yes' || val === true || val === 1;

        // type_*: 1=LumpSum, 2=PerPerson (inputMode: 1=lumpsum, 2=perPax)
        const lrType = 1; // Lawn Rent is always LumpSum
        const decType = 1; // Decoration is always LumpSum
        const cdType = quotationItems.coldDrink.inputMode === 1 ? 1 : 2;
        const mwType = quotationItems.mineralWater.inputMode === 1 ? 1 : 2;

        // subtotals
        const lrSub = Math.max(0, (Number(quotationItems.lawnRent.lumpsum) || 0) - (Number(quotationItems.lawnRent.discount) || 0));
        const decSub = Math.max(0, (Number(quotationItems.decoration.lumpsum) || 0) - (Number(quotationItems.decoration.discount) || 0));
        const cdRaw = isEnabled(quotationItems.coldDrink.enabled)
          ? (cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : (Number(quotationItems.coldDrink.rate) || 0) * g)
          : 0;
        const cdSub = Math.max(0, cdRaw - (Number(quotationItems.coldDrink.discount) || 0));
        const mwRaw = isEnabled(quotationItems.mineralWater.enabled)
          ? (mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : (Number(quotationItems.mineralWater.rate) || 0) * g)
          : 0;
        const mwSub = Math.max(0, mwRaw - (Number(quotationItems.mineralWater.discount) || 0));
        const pcSub = isEnabled(quotationItems.pantryCharges.enabled) ? Math.max(0, (Number(quotationItems.pantryCharges.rate) || 0) - (Number(quotationItems.pantryCharges.discount) || 0)) : 0;
        const ffSub = isEnabled(quotationItems.freshFlower.enabled) ? Math.max(0, (Number(quotationItems.freshFlower.rate) || 0) - (Number(quotationItems.freshFlower.discount) || 0)) : 0;

        let externalPayload;

        if (to === 'Site Visited') {
          externalPayload = {
            inquiry_type: 2,
            order_date: todayStr.split('-').reverse().join('-'),
            function_date: bookingData.eventDate.split('-').reverse().join('-'),
            validity_date: todayStr.split('-').reverse().join('-'),
            lawn_id: venueObj?.venue_id ?? venueObj?.numericId ?? venueNumericId,
            lawn_marge_status: 0,
            booking_timing: shiftId,
            customer_name: formData.clientName,
            mobile_one: formData.phone || "-",
            mobile_two: "-",
            address: bookingData.clientAddress || formData.clientAddress || "-",
            cnic: bookingData.clientCnic || formData.clientCnic || "-",
            function_id: eventTypeId,
            no_of_guest: g,
            allow_guest: g,
            quotation: Number(finalAmt) || 0,
            note: bookingData.notes || 'Tentative booking from CRM',
            grn_no: 0,
            receipt_no: 0,
            stock_type: 1,

            decoration: (Number(quotationItems.decoration.lumpsum) || 0) > 0 ? 1 : 0,
            stage: 1,
            stage_layer_id: 1,
            cold_drink: isEnabled(quotationItems.coldDrink.enabled) ? 1 : 0,
            mineral_water: isEnabled(quotationItems.mineralWater.enabled) ? 1 : 0,
            pantry_charges: isEnabled(quotationItems.pantryCharges.enabled) ? 1 : 0,
            fresh_flower: isEnabled(quotationItems.freshFlower.enabled) ? 1 : 0,

            service_lawn_rent: 'Lawn Rent',
            type_lawn_rent: lrType,
            lumpsum_lawn_rent: Number(quotationItems.lawnRent.lumpsum) || 0,
            rate_lawn_rent: 0,
            booked_qty_lawn_rent: g,
            discount_lawn_rent: Number(quotationItems.lawnRent.discount) || 0,
            discount_description_lawn_rent: '-',
            sub_total_lawn_rent: lrSub,

            service_decoration: 'Decoration',
            type_decoration: decType,
            lumpsum_decoration: Number(quotationItems.decoration.lumpsum) || 0,
            rate_decoration: 0,
            booked_qty_decoration: g,
            discount_decoration: Number(quotationItems.decoration.discount) || 0,
            discount_description_decoration: '-',
            sub_total_decoration: decSub,

            type_stage: 1,
            lumpsum_stage: 0,
            rate_stage: 0,
            booked_qty_stage: g,
            discount_stage: 0,
            discount_description_stage: '-',
            sub_total_stage: 0,

            service_cold_drink: 'Cold Drink',
            type_cold_drink: cdType,
            lumpsum_cold_drink: cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : cdRaw,
            rate_cold_drink: cdType === 2 ? (Number(quotationItems.coldDrink.rate) || 0) : 0,
            booked_qty_cold_drink: g,
            discount_cold_drink: Number(quotationItems.coldDrink.discount) || 0,
            discount_description_cold_drink: '-',
            sub_total_cold_drink: cdSub,

            service_mineral_water: 'Mineral Water',
            type_mineral_water: mwType,
            lumpsum_mineral_water: mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : mwRaw,
            rate_mineral_water: mwType === 2 ? (Number(quotationItems.mineralWater.rate) || 0) : 0,
            booked_qty_mineral_water: g,
            discount_mineral_water: Number(quotationItems.mineralWater.discount) || 0,
            discount_description_mineral_water: '-',
            sub_total_mineral_water: mwSub,

            service_pantry_charges: 'Pantry Charges',
            type_pantry_charges: 1,
            lumpsum_pantry_charges: Number(quotationItems.pantryCharges.rate) || 0,
            rate_pantry_charges: 0,
            booked_qty_pantry_charges: g,
            discount_pantry_charges: Number(quotationItems.pantryCharges.discount) || 0,
            discount_description_pantry_charges: '-',
            sub_total_pantry_charges: pcSub,

            service_fresh_flower: 'Flower Work',
            type_fresh_flower: 1,
            lumpsum_fresh_flower: Number(quotationItems.freshFlower.rate) || 0,
            rate_fresh_flower: 0,
            booked_qty_fresh_flower: g,
            discount_fresh_flower: Number(quotationItems.freshFlower.discount) || 0,
            discount_description_fresh_flower: '-',
            sub_total_fresh_flower: ffSub,
            
            additional_services: (quotationItems.addons || []).filter(a => a.name && (Number(a.lumpsum) || 0) > 0).map(a => {
              const ls = Number(a.lumpsum) || 0;
              const dsc = Number(a.discount) || 0;
              const sub = Math.max(0, ls - dsc);
              return {
                service_name: a.name,
                rate_type: 1,
                lumpsum: ls,
                rate: g > 0 ? Number((ls / g).toFixed(2)) : 0,
                booked_qty: g,
                discount: dsc,
                discount_description: '-',
                sub_total: sub
              };
            })
          };
        } else {
          externalPayload = {
            crm_id: `CRM-${lead.id}`,
          client_name: formData.clientName,
          client_mobile: formData.phone,
          email_address: bookingData.clientEmail || formData.email || '',
          cnic_no: bookingData.clientCnic,
          address: bookingData.clientAddress,
          venue_id: venueObj?.venue_id ?? venueObj?.numericId ?? venueNumericId,
          event_date: bookingData.eventDate,
          shift_id: shiftId,
          event_type_id: eventTypeId,
          guests: g,
          allow_guest: g,
          final_invoice_amount: Number(finalAmt),
          advance_received: Number(bookingData.advanceAmount) || 0,
          booking_notes: bookingData.notes || '',

          // Service flags (1=included, 0=not)
          decoration: (Number(quotationItems.decoration.lumpsum) || 0) > 0 ? 1 : 0,
          cold_drink: isEnabled(quotationItems.coldDrink.enabled) ? 1 : 0,
          mineral_water: isEnabled(quotationItems.mineralWater.enabled) ? 1 : 0,
          pantry_charges: isEnabled(quotationItems.pantryCharges.enabled) ? 1 : 0,
          fresh_flower: isEnabled(quotationItems.freshFlower.enabled) ? 1 : 0,

          // Lawn Rent
          service_lawn_rent: 'Lawn Rent',
          type_lawn_rent: lrType,
          lumpsum_lawn_rent: Number(quotationItems.lawnRent.lumpsum) || 0,
          rate_lawn_rent: 0,
          booked_qty_lawn_rent: g,
          discount_lawn_rent: Number(quotationItems.lawnRent.discount) || 0,
          discount_description_lawn_rent: '-',
          sub_total_lawn_rent: lrSub,

          // Decoration
          service_decoration: 'Decoration',
          type_decoration: decType,
          lumpsum_decoration: Number(quotationItems.decoration.lumpsum) || 0,
          rate_decoration: 0,
          booked_qty_decoration: g,
          discount_decoration: Number(quotationItems.decoration.discount) || 0,
          discount_description_decoration: '-',
          sub_total_decoration: decSub,

          // Cold Drink
          service_cold_drink: 'Cold Drink',
          type_cold_drink: cdType,
          lumpsum_cold_drink: cdType === 1 ? (Number(quotationItems.coldDrink.lumpsum) || 0) : cdRaw,
          rate_cold_drink: cdType === 2 ? (Number(quotationItems.coldDrink.rate) || 0) : 0,
          booked_qty_cold_drink: g,
          discount_cold_drink: Number(quotationItems.coldDrink.discount) || 0,
          discount_description_cold_drink: '-',
          sub_total_cold_drink: cdSub,

          // Mineral Water
          service_mineral_water: 'Mineral Water',
          type_mineral_water: mwType,
          lumpsum_mineral_water: mwType === 1 ? (Number(quotationItems.mineralWater.lumpsum) || 0) : mwRaw,
          rate_mineral_water: mwType === 2 ? (Number(quotationItems.mineralWater.rate) || 0) : 0,
          booked_qty_mineral_water: g,
          discount_mineral_water: Number(quotationItems.mineralWater.discount) || 0,
          discount_description_mineral_water: '-',
          sub_total_mineral_water: mwSub,

          // Pantry Charges
          service_pantry_charges: 'Pantry Charges',
          type_pantry_charges: 1,
          lumpsum_pantry_charges: Number(quotationItems.pantryCharges.rate) || 0,
          rate_pantry_charges: 0,
          booked_qty_pantry_charges: g,
          discount_pantry_charges: Number(quotationItems.pantryCharges.discount) || 0,
          discount_description_pantry_charges: '-',
          sub_total_pantry_charges: pcSub,

          // Flower Work
          service_fresh_flower: 'Flower Work',
          type_fresh_flower: 1,
          lumpsum_fresh_flower: Number(quotationItems.freshFlower.rate) || 0,
          rate_fresh_flower: 0,
          booked_qty_fresh_flower: g,
          discount_fresh_flower: Number(quotationItems.freshFlower.discount) || 0,
          discount_description_fresh_flower: '-',
          sub_total_fresh_flower: ffSub,
          
          additional_services: (quotationItems.addons || []).filter(a => a.name && (Number(a.lumpsum) || 0) > 0).map(a => {
            const ls = Number(a.lumpsum) || 0;
            const dsc = Number(a.discount) || 0;
            const sub = Math.max(0, ls - dsc);
            return {
              service_name: a.name,
              rate_type: 1,
              lumpsum: ls,
              rate: g > 0 ? Number((ls / g).toFixed(2)) : 0,
              booked_qty: g,
              discount: dsc,
              discount_description: '-',
              sub_total: sub
            };
          })
        };
        } // Close else block

        console.log('📤 Submitting to banquet API:', JSON.stringify(externalPayload, null, 2));

        // Warn if critical IDs are missing
        if (!externalPayload.venue_id && !externalPayload.lawn_id) {
          console.error('⚠️ venue_id/lawn_id is null — set venue Banquet ID in Admin Panel → App Settings → Venues');
          throw new Error('Venue Banquet ID not set. Please go to Admin Panel → App Settings → Venues and set the numeric ID.');
        }
        if (!externalPayload.event_type_id && !externalPayload.function_id) {
          console.error('⚠️ event_type_id is null — set event type Banquet ID in Admin Panel → App Settings → Event Types');
          throw new Error('Event Type Banquet ID not set. Please go to Admin Panel → App Settings → Event Types and set the numeric ID.');
        }

        if (to === 'Site Visited') {
          const apiRes = await bookingService.submitTentativeToExternal(externalPayload);
          console.log('✅ Tentative Booking submitted to banquet system successfully. API Response:', apiRes);
          if (apiRes && (apiRes.status === false || apiRes.success === false || apiRes.error)) {
            console.error('BMS returned an error payload despite HTTP 200:', apiRes);
            throw new Error(apiRes.error || apiRes.message || JSON.stringify(apiRes));
          }
        } else {
          await bookingService.submitToExternal(externalPayload);
          console.log('✅ Booking submitted to banquet system successfully');
        }
      } catch (extError) {
        // Block the CRM flow if external API fails
        console.error('External banquet API error:', extError);
        setToast({ message: 'Banquet System Error: ' + (extError.message || 'Failed to post booking.'), type: 'error' });
        throw extError; // Throw to cancel the CRM update
      }

      // ── BMS Submission Succeeded (or skipped): Update CRM ──
      
      // Save to database — include pending formData edits + booking details in ONE request
      await onSave(lead.id, {
        ...formData,
        stage: to,
        venue: bookingData.venue,
        eventDate: bookingData.eventDate,
        eventType: bookingData.eventType,
        guests: parseInt(bookingData.guests) || null,
        finalAmount: Number(finalAmt),
        quotationAmount: Number(finalAmt),
        quotationItems,
        shift: bookingData.shift,
        clientCnic: bookingData.clientCnic,
        clientAddress: bookingData.clientAddress,
        bookingNotes: bookingData.notes,
        notes: bookingData.notes ? (formData.notes ? formData.notes + '\n\nBooking Note: ' + bookingData.notes : formData.notes) : formData.notes,
        bookedAt: new Date().toISOString(),
        bookedBy: currentUser.name,
        stageUpdatedAt: new Date().toISOString(),
        stageUpdatedBy: currentUser.name,
        ...(to === 'Won' && lead.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      });

      // Log ONE combined activity — includes booking details and any pending field edits
      const activityData = {
        from,
        to,
        note: bookingData.notes || null,
        editedFields: pendingChangedFields.length > 0 ? pendingChangedFields.join(', ') : null,
        bookingDetails: {
          venue: bookingData.venue,
          eventDate: bookingData.eventDate,
          finalAmount: finalAmt,
          advanceAmount: bookingData.advanceAmount || 0,
          eventType: bookingData.eventType,
          guests: bookingData.guests,
          shift: bookingData.shift,
          clientCnic: bookingData.clientCnic,
          clientAddress: bookingData.clientAddress,
        }
      };

      await leadsService.addNote(lead.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE,
        userId: currentUser.id
      });
      fetchTimeline();

      // Optimistic UI update
      setFormData(prev => ({
        ...prev,
        stage: to,
        venue: bookingData.venue,
        eventDate: bookingData.eventDate,
        eventType: bookingData.eventType,
        guests: bookingData.guests,
        quotationAmount: Number(finalAmt),
        shift: bookingData.shift,
        clientCnic: bookingData.clientCnic,
        clientAddress: bookingData.clientAddress,
        bookingNotes: bookingData.notes,
        notes: bookingData.notes ? (prev.notes ? prev.notes + '\n\nBooking Note: ' + bookingData.notes : bookingData.notes) : prev.notes,
        bookedAt: new Date().toISOString(),
        bookedBy: currentUser.name,
        ...(to === 'Won' && lead.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      }));
      setBookingConfirm(null);
      setActiveTab('activity');

    } catch (error) {
      console.error('Error in booking flow:', error);
      // Revert stage on failure
      setFormData(prev => ({ ...prev, stage: from }));
    } finally {
      setIsStageChanging(false);
    }
  };

  const handleBookingCancel = () => {
    setBookingConfirm(null);
    setVenueCheckStatus(null);
    setVenueBookedInfo(null);
    // Kanban drag-to-Won flow: auto-close LeadDetailModal so user returns
    // directly to the board with the card still in its original column.
    // Stage pill flow: keep LeadDetailModal open (user stays in edit context).
    if (pendingTransition?.source === 'kanban') {
      setPendingTransition(null);
      onClose();
    }
  };

  // ── Venue Availability Check ──
  const checkVenueAvailability = async (venueId, eventDate, shiftName) => {
    if (!venueId || !eventDate || !shiftName) {
      setVenueCheckStatus(null);
      setVenueBookedInfo(null);
      return;
    }
    setVenueCheckStatus('checking');
    setVenueBookedInfo(null);
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = import.meta.env.VITE_API_URL ||
        (['localhost', '127.0.0.1'].includes(window.location.hostname)
          ? 'http://localhost:3055/api'
          : 'https://area51crm.inplsoftwares.com/api');
      const res = await fetch(
        `${API_BASE_URL}/bookings/banquet-list?from_date=${eventDate}&to_date=${eventDate}`,
        { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      const data = await res.json();
      const bookingsList = data.data || [];
      // Find selected venue name and numeric banquet ID
      const venueObj = (venues && venues.length > 0 ? venues : []).find(v => v.id === venueId);
      const venueName = venueObj?.name || '';
      const venueNumericId = venueObj?.venue_id ?? venueObj?.numericId ?? venueObj?.venueId ?? null;

      // Helper: extract meaningful words (3+ chars, not pure numbers)
      const meaningfulWords = (str) =>
        str.toLowerCase().split(/[\s_\-]+/).filter(w => w.length >= 3 && isNaN(w));

      // Check if any booking on this date matches the venue AND shift
      const conflict = bookingsList.find(b => {
        const lawnName = (b.lawn_name || '').toLowerCase().trim();
        const vName = venueName.toLowerCase().trim();
        if (!lawnName || !vName) return false;

        let isVenueMatch = false;
        // Tier 1: numeric venue_id match (most reliable)
        if (venueNumericId && b.lawn_id && Number(b.lawn_id) === Number(venueNumericId)) isVenueMatch = true;
        // Tier 2: exact or substring match
        else if (lawnName === vName || lawnName.includes(vName) || vName.includes(lawnName)) isVenueMatch = true;
        // Tier 3: word-level fuzzy match
        else {
          const words1 = meaningfulWords(lawnName);
          const words2 = meaningfulWords(vName);
          isVenueMatch = words1.some(w1 => words2.some(w2 => w1.includes(w2) || w2.includes(w1)));
        }

        if (!isVenueMatch) return false;

        // Shift match check
        const bShift = (b.booking_timing_name || '').toLowerCase().trim();
        const targetShift = (shiftName || '').toLowerCase().trim();
        
        if (targetShift === 'day' || targetShift === 'morning') {
           if (bShift !== 'day' && bShift !== 'morning') return false;
        } else if (targetShift === 'night' || targetShift === 'evening') {
           if (bShift !== 'night' && bShift !== 'evening') return false;
        } else {
           if (bShift !== targetShift) return false;
        }
        
        return true;
      });
      if (conflict) {
        setVenueCheckStatus('booked');
        setVenueBookedInfo({
          clientName: conflict.customer_name || 'Unknown',
          orderNo: conflict.order_no || '',
          shift: conflict.booking_timing_name || '',
          functionName: conflict.function_name || '',
        });
      } else {
        setVenueCheckStatus('available');
        setVenueBookedInfo(null);
      }
    } catch (err) {
      console.error('Venue availability check failed:', err);
      setVenueCheckStatus(null);
    }
  };
  const isBookingFormValid = bookingData.venue && bookingData.eventDate && Number(bookingData.guests) > 0 && (quotationCalc.total || bookingData.finalAmount) && venueCheckStatus !== 'booked' && (bookingConfirm?.to === 'Site Visited' ? true : (bookingData.clientCnic && bookingData.clientAddress));

  const sourceOptions = useMemo(() => {
    const list = [...(sources || [])];
    if (formData.source && !list.find((s) => s.name === formData.source)) {
      // Use a unique ID based on the source name to avoid collisions
      list.push({ id: `custom-${formData.source}`, name: formData.source });
    }
    return list;
  }, [sources, formData.source]);

  // --- Read-Only Check ---
  // If lead is Completed, we lock the stage/status changes.
  // DISABLED WON & LOST LOCKING (allow moving out of Lost if moved by mistake)
  const isLockedStatus = ['Completed'].includes(formData.status) || ['Completed'].includes(formData.stage);

  const isAdminOrOwner = ['Admin', 'Owner'].includes(currentUser?.role);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in relative">

        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-start gap-3 sticky top-0 z-20">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{formData.clientName}</h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${formData.stage === 'Won' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  formData.stage === 'Lost' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-slate-100 text-slate-700 border-slate-200'
                }`}>
                {formData.stage}
              </span>
              {isLockedStatus && (
                <span className="flex items-center gap-1 px-2.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded-full text-[10px] font-bold whitespace-nowrap">
                  <Lock className="w-3 h-3 shrink-0" /> {t('locked') || 'Locked'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
              {lead.createdAt && (
                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                  {/* Labelled, because a bare timestamp under a clock icon sits
                      next to an event date, a quote and a follow-up date, and
                      nothing said which of them it was. */}
                  <Clock className="w-3 h-3" />
                  <span className="text-gray-400 font-normal">{t('created') || 'Created'}</span>
                  {new Date(lead.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {/* A DM identifier under a phone icon reads as "we have their
                  number" — the exact belief that stops a rep asking for it. */}
              {formData.phone && !isPlaceholderPhone(formData.phone) && (
                <span className="flex items-center gap-1 font-mono font-bold text-gray-700">
                  <Phone className="w-3 h-3" />{formData.phone}
                </span>
              )}
              {isPlaceholderPhone(formData.phone) && (
                <span className="flex items-center gap-1 font-semibold text-amber-600 whitespace-nowrap">
                  <AlertCircle className="w-3 h-3" />{t('noPhoneYet') || 'No phone number yet'}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isAdminOrOwner && onDelete && (
              <button 
                onClick={() => setShowDeleteConfirm(true)} 
                className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors" 
                title="Delete Lead"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            <button onClick={() => onOpenAi(lead)} className="p-2 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors" title="Ask AI Assistant">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* ── Stage pills ── */}
        <div className="bg-slate-50 border-b border-gray-200 px-3 sm:px-6 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          {STAGES.map((s) => (
            <button
              key={s}
              onClick={() => handleStageClick(s)}
              disabled={isLockedStatus}
              className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all border whitespace-nowrap ${isLockedStatus
                  ? 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200'
                  : formData.stage === s
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm scale-105'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                }`}
            >
              {t(s) || s}
            </button>
          ))}
        </div>

        {/* ── Stage Change Confirm ── */}
        {stageChangeConfirm && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 w-full max-w-sm animate-scale-in">
              <h3 className="text-base font-bold text-gray-900 mb-1">{t('stageChangeConfirmTitle') || 'Confirm Stage Change'}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('moveLeadFrom') || 'Move lead from '}
                <span className="font-semibold text-gray-800"> {stageChangeConfirm.from} </span>  
                {t('moveLeadTo') || ' to '}
                <span className="font-semibold text-blue-600"> {stageChangeConfirm.to}</span>?
              </p>
              {/* Losing a lead asks why — one required click. Reasons are
                  grouped, but the salesperson only ever sees labels. */}
              {stageChangeConfirm.to === 'Lost' && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t('lossReasonLabel') || 'Why was this lead lost?'} <span className="text-red-500">*</span>
                  </label>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {LOSS_REASONS.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setLossReason(r.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${lossReason === r.id
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {t('loss_' + r.id) || r.label}
                      </button>
                    ))}
                  </div>
                  {lossReason === 'postponed' && (
                    <p className="text-[11px] text-amber-600 mt-1.5 leading-snug">
                      {t('lossPostponedHint') || 'Set a follow-up date on the lead before saving — postponed events are the warmest leads you have.'}
                    </p>
                  )}
                </div>
              )}
              {/* Entering Quoted requires the figure the customer was given.
                  Prefilled from the builder when it has been used. */}
              {stageChangeConfirm.to === 'Quoted' && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t('amountQuotedLabel') || 'What amount did you quote?'} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">PKR</span>
                    <input
                      type="number"
                      autoFocus
                      value={quotedAmount}
                      onChange={(e) => setQuotedAmount(e.target.value)}
                      placeholder="1,250,000"
                      className="w-full pl-12 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  {quotationCalc.total > 0 && Number(quotedAmount) === quotationCalc.total ? (
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      {t('amountFromBuilder') || 'From the quotation builder. Change it here if you quoted a different figure.'}
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      {t('amountQuotedHint') || 'A verbal or WhatsApp quote counts — type the figure you gave.'}
                    </p>
                  )}
                  {/* Not a blocker. A required field invites a throwaway number,
                      and a fake figure is worse than a blank one because it
                      silently inflates pipeline value. Every real quote on
                      record is above Rs 650,000, so flag anything far below. */}
                  {Number(quotedAmount) > 0 && Number(quotedAmount) < 100000 && (
                    <p className="text-[11px] text-amber-600 mt-1 leading-snug">
                      {t('amountQuotedLow') || 'That is far below any quote on record — check the figure before confirming.'}
                    </p>
                  )}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {stageChangeConfirm.to === 'Lost' && lossReason === 'other'
                    ? (t('notesRequired') || 'Notes (Required)')
                    : (t('notesOptional') || 'Notes (Optional)')}
                </label>
                <textarea
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm h-20 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={stageChangeNote}
                  onChange={(e) => setStageChangeNote(e.target.value)}
                  placeholder={stageChangeConfirm.to === 'Lost' && lossReason === 'competitor'
                    ? (t('lossCompetitorPlaceholder') || 'Which venue did they choose?')
                    : 'Add any notes about this stage change...'}
                />
              </div>
              <p className="text-xs text-gray-400 mb-4">{t('stageChangeTimelineLog') || 'This will be logged in the timeline.'}</p>
              <div className="flex gap-2 justify-end">
                <button onClick={handleStageChangeCancel} disabled={isStageChanging}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50">
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleStageChangeConfirm}
                  disabled={isStageChanging
                    || (stageChangeConfirm.to === 'Lost' && (!lossReason || (lossReason === 'other' && !stageChangeNote.trim())))
                    || (stageChangeConfirm.to === 'Quoted' && !(Number(quotedAmount) > 0))}
                  className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center">
                  {isStageChanging ? <Loader className="w-4 h-4 animate-spin" /> : (t('confirm') || 'Confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirm Modal ── */}
        {showDeleteConfirm && (
          <DeleteConfirmModal 
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => {
              setShowDeleteConfirm(false);
              onDelete(lead.id);
            }}
          />
        )}

        {/* ── Booking Confirmation Modal ── */}
        {bookingConfirm && (
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-0 sm:p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) handleBookingCancel(); }}
            onTouchStart={(e) => { if (e.target === e.currentTarget) handleBookingCancel(); }}
          >
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl animate-scale-in flex flex-col max-h-[92vh] sm:max-h-[88vh]">
              {/* Header */}
              <div className="bg-white border-b border-gray-200 px-5 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {bookingConfirm.to === 'Site Visited' ? 'Confirm Tentative Booking' : t('confirmBookingTitle')}
                    </h3>
                    <p className="text-xs text-gray-500">{t('verifyDetails')}</p>
                  </div>
                </div>
                <button onClick={handleBookingCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
                {/* Client Info */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{formData.clientName}</p>
                    <p className="text-xs text-gray-500">{formData.phone}</p>
                  </div>
                </div>

                {/* CNIC and Address */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        CNIC No <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength="15"
                        className={`w-full p-2.5 border rounded-lg text-sm ${!bookingData.clientCnic ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={bookingData.clientCnic}
                        onChange={(e) => setBookingData({ ...bookingData, clientCnic: e.target.value })}
                        placeholder="e.g., 42101-1234567-1"
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2.5 border rounded-lg text-sm ${!bookingData.clientAddress ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={bookingData.clientAddress}
                        onChange={(e) => setBookingData({ ...bookingData, clientAddress: e.target.value })}
                        placeholder="Client Address"
                        disabled={isLockedStatus}
                      />
                    </div>
                  </div>
                )}

                {/* Venue + Event Date — same row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('venue')} <span className="text-red-500">*</span>
                    </label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className={`w-full p-2.5 border rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-left flex justify-between items-center ${!bookingData.venue ? 'border-red-300' : 'border-gray-200'} ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isLockedStatus}
                        >
                          {bookingData.venue ? ((venues && venues.length > 0 ? venues : VENUES).find(v => v.id === bookingData.venue)?.name || bookingData.venue) : (t('selectVenue') || 'Select Venue')}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([bookingData.venue])}
                          selectionMode="single"
                          onSelectionChange={(keys) => {
                            const newVenue = Array.from(keys)[0];
                            setBookingData({ ...bookingData, venue: newVenue });
                            checkVenueAvailability(newVenue, bookingData.eventDate, bookingData.shift);
                          }}
                        >
                          {(venues && venues.length > 0 ? venues.filter(v => v.isLive !== false) : VENUES).map((v) => (
                            <DropdownItem key={v.id} id={v.id}>{v.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                    {!bookingData.venue && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {t('pleaseSelectVenue')}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('eventDate')} <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      className={`${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                      error={!bookingData.eventDate ? ' ' : null}
                      value={bookingData.eventDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setBookingData({ ...bookingData, eventDate: newDate });
                        checkVenueAvailability(bookingData.venue, newDate, bookingData.shift);
                      }}
                      disabled={isLockedStatus}
                      placement="bottom"
                    />
                  </div>
                </div>


                {/* Shift Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t('shift')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    {['Morning', 'Evening'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setBookingData({ ...bookingData, shift: s });
                          checkVenueAvailability(bookingData.venue, bookingData.eventDate, s);
                        }}
                        className={`flex-1 p-2.5 rounded-lg border-2 font-medium transition-all ${bookingData.shift === s
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                      >
                        {t(s.toLowerCase() + 'Shift') || s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Venue Availability Banner ── */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <>
                    {bookingData.shift && venueCheckStatus === 'checking' && (
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl animate-pulse">
                        <svg className="w-4 h-4 text-blue-500 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <p className="text-sm text-blue-700 font-medium">Checking venue availability...</p>
                      </div>
                    )}
                    {bookingData.shift && venueCheckStatus === 'booked' && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 rounded-xl">
                        <div className="shrink-0 p-1.5 bg-red-100 rounded-lg">
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-red-700">This venue is already booked on {bookingData.eventDate} in the {bookingData.shift} shift.</p>
                      </div>
                    )}
                    {bookingData.shift && venueCheckStatus === 'available' && bookingData.venue && bookingData.eventDate && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="shrink-0 p-1.5 bg-emerald-100 rounded-lg">
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-emerald-700">Venue is available on this date in the {bookingData.shift} shift.</p>
                      </div>
                    )}
                  </>
                )}


                {/* Event Type & Guests */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('eventType')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isLockedStatus}
                        >
                          {bookingData.eventType || t('selectEventType')}
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([bookingData.eventType])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setBookingData({ ...bookingData, eventType: Array.from(keys)[0] })}
                        >
                          {(eventTypes || EVENT_TYPES).map((et, i) => {
                            const etName = typeof et === 'string' ? et : et.name;
                            return (
                              <DropdownItem key={`booking-et-${i}`} id={etName}>
                                {etName}
                              </DropdownItem>
                            );
                          })}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('guests')}</label>
                    <input
                      type="text"
                      className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                      value={bookingData.guests}
                      onChange={(e) => setBookingData({ ...bookingData, guests: e.target.value.replace(/[^0-9]/g, '') })}
                      placeholder={t('guestsPlaceholder')}
                      disabled={isLockedStatus}
                    />
                  </div>
                </div>

                {/* Quotation Builder */}
                <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('qbTitle') || 'Quotation Builder'}</h3>
                    </div>
                    <span className="text-[11px] text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                      Guests: <span className="text-blue-600 font-bold">{quotationCalc.g}</span>
                    </span>
                  </div>

                  <div className="overflow-x-auto -mx-1 px-1 pb-2"><div className="min-w-[700px] sm:min-w-0">
                  {/* Column Labels */}
                  <div className="grid grid-cols-12 gap-x-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">
                    <div className="col-span-3">{t('qbService') || 'Service'}</div>
                    <div className="col-span-1 text-center">{t('qbStatus') || 'Status'}</div>
                    <div className="col-span-2 text-center">{t('qbAmtRate') || 'Amt / Rate'}</div>
                    <div className="col-span-1 text-center">{t('qbQty') || 'Qty'}</div>
                    <div className="col-span-2 text-center">{t('qbSubtotal') || 'Subtotal'}</div>
                    <div className="col-span-1 text-center">{t('qbDisc') || 'Disc'}</div>
                    <div className="col-span-2 text-right">{t('qbNet') || 'Net'}</div>
                  </div>

                  <div className="space-y-2">

                    {/* ── Lawn Rent ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbLawnRent') || 'Lawn Rent'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.lawnRent.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.lawnRent.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.lawnRent.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.lrSub.toLocaleString()}</div>
                    </div>

                    {/* ── Decoration ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbDecoration') || 'Decoration'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.decoration.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.decoration.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.decoration.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.decSub.toLocaleString()}</div>
                    </div>

                    {/* ── Cold Drink ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbColdDrink') || 'Cold Drink'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.coldDrink.enabled === 'yes'
                              ? (quotationItems.coldDrink.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.coldDrink.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.coldDrink.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            quotationItems.coldDrink.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.coldDrink.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.coldDrink.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.coldDrink.enabled === 'yes'
                            ? (quotationItems.coldDrink.inputMode === 1
                              ? (Number(quotationItems.coldDrink.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.coldDrink.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.cdSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.coldDrink.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.coldDrink.inputMode === 1 && quotationCalc.cdPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.cdPerPax % 1 === 0 ? quotationCalc.cdPerPax.toLocaleString() : quotationCalc.cdPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.coldDrink.inputMode !== 1 && quotationItems.coldDrink.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Mineral Water ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbMineralWater') || 'Mineral Water'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.mineralWater.enabled === 'yes'
                              ? (quotationItems.mineralWater.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.mineralWater.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.mineralWater.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.mineralWater.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            quotationItems.mineralWater.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.mineralWater.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.mineralWater.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : quotationItems.mineralWater.enabled === 'complementary' ? (
                            <span className="text-[10px] text-green-600 font-semibold text-center block">4 Dispensers</span>
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.mineralWater.enabled === 'yes'
                            ? (quotationItems.mineralWater.inputMode === 1
                              ? (Number(quotationItems.mineralWater.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.mineralWater.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.mwSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.mineralWater.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.mineralWater.inputMode === 1 && quotationCalc.mwPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.mwPerPax % 1 === 0 ? quotationCalc.mwPerPax.toLocaleString() : quotationCalc.mwPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.mineralWater.inputMode !== 1 && quotationItems.mineralWater.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Pantry Charges ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbPantryCharges') || 'Pantry Charges'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.pantryCharges.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.pantryCharges.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.pantryCharges.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (Number(quotationItems.pantryCharges.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.pantryCharges.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.pantryCharges.enabled === 'yes' ? quotationCalc.pcSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* ── Flower Work ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbFlowerWork') || 'Flower Work'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.freshFlower.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.freshFlower.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.freshFlower.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.freshFlower.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : quotationItems.freshFlower.enabled === 'complementary' ? (
                          <span className="text-[10px] text-green-600 font-semibold text-center block">{t('qbOnStage') || 'On Stage'}</span>
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.freshFlower.enabled === 'yes' ? (Number(quotationItems.freshFlower.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.freshFlower.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.freshFlower.enabled === 'yes' ? quotationCalc.ffSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* Addons */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-bold text-gray-600 uppercase">{t('qbAddons') || 'Addons'}</h4>
                            <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, addons: [...(p.addons || []), { id: Date.now(), name: '', lumpsum: '', discount: '' }] }))}
                                disabled={isLockedStatus}
                                className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                            >
                                + Add More
                            </button>
                        </div>
                        
                        {(quotationItems.addons || []).map((addon, index) => (
                            <div key={addon.id || index} className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100 mt-2">
                                <div className="col-span-3">
                                    <input type="text" className="w-full p-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:outline-none" placeholder={t('qbAddonName') || 'Addon Name'}
                                        value={addon.name}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].name = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-1 flex justify-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span></div>
                                <div className="col-span-2">
                                    <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                                        value={addon.lumpsum}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].lumpsum = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                                    {(Number(addon.lumpsum) || 0).toLocaleString()}
                                </div>
                                <div className="col-span-1">
                                    <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                                        value={addon.discount}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].discount = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">
                                    {Math.max(0, (Number(addon.lumpsum) || 0) - (Number(addon.discount) || 0)).toLocaleString()}
                                </div>
                                <div className="col-span-1 text-right">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if(isLockedStatus) return;
                                            const newAddons = quotationItems.addons.filter((_, i) => i !== index);
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus}
                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Grand Total */}
                    <div className="flex items-center justify-between bg-blue-600 text-white rounded-lg px-4 py-3 mt-1">
                      <span className="text-xs font-bold uppercase tracking-widest">{t('qbGrandTotal') || 'Grand Total'}</span>
                      <span className="text-base font-bold">PKR {quotationCalc.total.toLocaleString()}</span>
                    </div>
                </div>
              </div>
                  </div>
                </div>

                {/* Final Amount + Advance — same row */}
                {bookingConfirm.to !== 'Site Visited' && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-md mb-3" dangerouslySetInnerHTML={{ __html: t('lawnRentMandatoryNote') || '<b>Note:</b> Lawn rent is mandatory for booking confirmation.' }} />
                )}
                <div className={`grid ${bookingConfirm.to === 'Site Visited' ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                  {/* Final Invoice Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {t('finalInvoiceAmount')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">PKR</span>
                      <input
                        type="number" onWheel={(e) => e.target.blur()}
                        className={`w-full p-2.5 pl-12 border border-gray-200 rounded-lg text-sm bg-gray-50 cursor-not-allowed`}
                        value={quotationCalc.total || bookingData.finalAmount}
                        readOnly
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Advance Received */}
                  {bookingConfirm.to !== 'Site Visited' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('advanceReceived')}</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">PKR</span>
                        <input
                          type="number" onWheel={(e) => e.target.blur()}
                          className={`w-full p-2.5 pl-12 border border-gray-200 rounded-lg text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={bookingData.advanceAmount}
                          onChange={(e) => setBookingData({ ...bookingData, advanceAmount: e.target.value })}
                          placeholder="0"
                          disabled={isLockedStatus}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {bookingConfirm.to !== 'Site Visited' && (quotationCalc.total || bookingData.finalAmount) && bookingData.advanceAmount && (
                  <p className="text-xs text-gray-500 -mt-1">
                    {t('balanceDue')} PKR {(Number(quotationCalc.total || bookingData.finalAmount) - Number(bookingData.advanceAmount)).toLocaleString()}
                  </p>
                )}

                {/* Booking Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{t('bookingNotes')}</label>
                  <textarea
                    className={`w-full p-2.5 border border-gray-200 rounded-lg text-sm h-20 resize-none ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                    placeholder={t('bookingNotesPlaceholder')}
                    disabled={isLockedStatus}
                  />
                </div>

                {/* Summary */}
                {isBookingFormValid && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">{t('bookingSummary')}</p>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p><span className="text-blue-500">{t('venue')}:</span> {(venues && venues.length > 0 ? venues : VENUES).find(v => v.id === bookingData.venue)?.name || bookingData.venue}</p>
                      <p><span className="text-blue-500">{t('date')}:</span> {formatDate(bookingData.eventDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      <p><span className="text-blue-500">{t('amount')}:</span> PKR {Number(quotationCalc.total || bookingData.finalAmount).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-4 sm:px-5 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end rounded-b-2xl flex-shrink-0">
                <button
                  onClick={handleBookingCancel}
                  disabled={isStageChanging || isLockedStatus}
                  className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleBookingConfirm}
                  disabled={!isBookingFormValid || isStageChanging || isLockedStatus}
                  className={`px-5 py-2.5 text-sm rounded-xl font-semibold transition-colors flex items-center justify-center min-w-[140px] gap-2 ${!isBookingFormValid || isStageChanging || isLockedStatus
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                    }`}
                >
                  {isStageChanging ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <><CheckCircle className="w-4 h-4" />{bookingConfirm?.to === 'Site Visited' ? 'Submit Tentative' : t('confirmBookingTitle')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
          {/* ── Sidebar tabs ── */}
          <div className="w-full sm:w-48 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 p-2 sm:p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible no-scrollbar shrink-0">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'details' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                }`}
            >
              <Edit3 className="w-4 h-4 shrink-0" />{t('details')}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'activity' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                }`}
            >
              <Clock className="w-4 h-4 shrink-0" />{t('timeline')}
              {timeline.length > 0 && (
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {timeline.length}
                </span>
              )}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-8">
            {activeTab === 'details' && (
              <div className="max-w-2xl mx-auto space-y-6">
                {/* ── Contact ── the person, not the deal. Lives on its own
                    record, so it saves on its own button and reports its own
                    errors. */}
                {contactDraft && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" /> {t('contactHeading') || 'Contact'}
                      </h3>
                      {contactDirty && (
                        <button
                          onClick={handleSaveContact}
                          disabled={contactSaving || !!phoneProblem(contactDraft.phone)}
                          className="px-3 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60 flex items-center gap-1.5 transition-colors"
                        >
                          {contactSaving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          {t('saveContact') || 'Save contact'}
                        </button>
                      )}
                      {!contactDirty && contactSaved && (
                        <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> {t('saved') || 'Saved'}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{t('firstName') || 'First name'}</label>
                        <input
                          className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={contactDraft.firstName}
                          onChange={(e) => { setContactDraft({ ...contactDraft, firstName: e.target.value }); setContactSaved(false); }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{t('lastName') || 'Last name'}</label>
                        <input
                          className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={contactDraft.lastName}
                          onChange={(e) => { setContactDraft({ ...contactDraft, lastName: e.target.value }); setContactSaved(false); }}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          {t('phone') || 'Phone'}
                          {!contactDraft.phone && <span className="ml-2 text-[11px] font-medium text-amber-600">{t('phoneMissingTag') || 'not set'}</span>}
                        </label>
                        <input
                          className={`w-full p-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none ${contactDraft.phone ? 'border-gray-200 focus:border-blue-500' : 'border-amber-300 bg-amber-50/40 focus:border-blue-500'}`}
                          value={contactDraft.phone}
                          placeholder={t('phonePlaceholder') || '0314 2781759'}
                          onChange={(e) => { setContactDraft({ ...contactDraft, phone: e.target.value }); setContactSaved(false); setContactConflict(null); }}
                        />
                        {/* The nudge. Not a warning about something broken — a
                            prompt to do the one thing that keeps this lead
                            whole when the conversation moves to WhatsApp. */}
                        {!contactDraft.phone && (
                          <p className="text-[11px] text-amber-700 mt-1.5 leading-snug">
                            {t('phoneMissingHint') || 'No number yet. Add it as soon as they send it — otherwise their first WhatsApp reply arrives from an unknown number and starts a second lead under a different rep.'}
                          </p>
                        )}
                        {!!contactDraft.phone && phoneProblem(contactDraft.phone) && (
                          <p className="text-[11px] text-red-600 mt-1.5 leading-snug font-medium">
                            {phoneProblem(contactDraft.phone)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* A DM identifier is not a phone number and should not sit
                        in a field labelled Phone pretending to be one. */}
                    {isPlaceholderPhone(lead?.contact?.phone) && (
                      <p className="text-[11px] text-gray-400 mt-3 font-mono">
                        {String(lead.contact.phone).startsWith('fb:') ? 'Messenger' : 'Instagram'} · {lead.contact.phone}
                      </p>
                    )}

                    {contactConflict && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs text-amber-900 font-semibold">
                          {(t('phoneTakenBy') || 'That number already belongs to {name}').replace('{name}', contactConflict.name || 'another contact')}
                          {contactConflict.assignedTo ? ` — ${contactConflict.assignedTo}` : ''}
                          {contactConflict.leadStatus ? ` · ${contactConflict.leadStatus}` : ''}
                        </p>
                        <p className="text-[11px] text-amber-700 mt-1 leading-snug">
                          {t('phoneTakenHint') || 'They already exist in the CRM, most likely because they replied on WhatsApp before the number reached this contact. Open that lead and carry on there.'}
                        </p>
                        {contactConflict.leadId && (
                          <button
                            onClick={() => { onClose(); setTimeout(() => navigate(`?leadId=${contactConflict.leadId}`), 150); }}
                            className="mt-2 text-xs font-bold text-blue-700 hover:underline"
                          >
                            {t('openThatLead') || 'Open that lead'} →
                          </button>
                        )}
                      </div>
                    )}
                    {contactError && <p className="text-xs text-red-600 mt-3">{contactError}</p>}
                  </div>
                )}

                {/* Meta Campaign Info - Show if lead has meta data */}
                {formData.meta && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-blue-500" />
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">
                        {t('metaCampaignInfo')}
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {formData.meta.campaign_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('campaign')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.campaign_name}</div>
                        </div>
                      )}
                      {formData.meta.adset_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('adSet')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.adset_name}</div>
                        </div>
                      )}
                      {formData.meta.ad_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('ad')}</span>
                          <div className="font-medium text-gray-900 truncate" title={formData.meta.ad_name}>
                            {formData.meta.ad_name}
                          </div>
                        </div>
                      )}
                      {formData.meta.platform && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('platform')}</span>
                          <div className="font-medium text-gray-900 capitalize">{formData.meta.platform}</div>
                        </div>
                      )}
                      {formData.meta.form_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('form')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.form_name}</div>
                        </div>
                      )}
                      {formData.meta.city && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('city')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.city}</div>
                        </div>
                      )}
                    </div>

                    {/* Custom Fields Chips */}
                    {(formData.budgetRange || formData.meta.budget_range || formData.meta.event_type) && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-200">
                        {(formData.budgetRange || formData.meta.budget_range) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            <DollarSign className="w-3 h-3" />
                            {formData.budgetRange || formData.meta.budget_range}
                          </span>
                        )}
                        {formData.meta.event_type && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {formData.meta.event_type}
                          </span>
                        )}
                        {formData.meta.guest_count && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <Users className="w-3 h-3" />
                            {formData.meta.guest_count} {t('guests').toLowerCase()}
                          </span>
                        )}
                        {formData.meta.event_date && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            <Calendar className="w-3 h-3" />
                            {formData.meta.event_date}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">
                    {t('eventInfo')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('eventDate')}</label>
                      <DatePicker
                        className={`mt-1 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.eventDate ? new Date(formData.eventDate).toISOString().split('T')[0] : ''}
                        onChange={(e) =>
                          setFormData({ ...formData, eventDate: e.target.value })
                        }
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('shift')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            {formData.shift || (t('selectShift') === 'selectShift' ? 'Select Shift' : t('selectShift'))}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.shift])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, shift: Array.from(keys)[0] })}
                          >
                            <DropdownItem id="Morning">{t('morningShift') === 'morningShift' ? 'Morning Shift' : t('morningShift')}</DropdownItem>
                            <DropdownItem id="Evening">{t('eveningShift') === 'eveningShift' ? 'Evening Shift' : t('eveningShift')}</DropdownItem>
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('guests')}</label>
                      <input
                        type="number" onWheel={(e) => e.target.blur()}
                        className={`w-full p-2 border rounded-lg text-sm mt-1 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.guests || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, guests: e.target.value.replace(/[^0-9]/g, '') })
                        }
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('clientBudget')}</label>
                      <input
                        className={`w-full p-2 border rounded-lg text-sm mt-1 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.clientBudget || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, clientBudget: Number(e.target.value) })
                        }
                        type="number" onWheel={(e) => e.target.blur()}
                        placeholder={t('clientBudgetPlaceholder')}
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('quotationAmount')}</label>
                      <div className="w-full p-2 border border-blue-200 rounded-lg text-sm mt-1 bg-blue-50 text-blue-800 font-bold flex items-center justify-between">
                        <span>PKR {(quotationCalc.total || formData.quotationAmount || 0).toLocaleString()}</span>
                        <span className="text-[10px] font-normal text-blue-400">auto-calculated ↓</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('manager')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border rounded-lg text-sm mt-1 bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!['admin', 'owner'].includes(currentUser?.role?.toLowerCase()) || isLockedStatus}
                          >
                            {formData.manager || t('selectManager')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.manager])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, manager: Array.from(keys)[0] })}
                          >
                            {(managers || MANAGERS).map((m, i) => (
                              <DropdownItem key={`${m}-${i}`} id={m}>
                                {m}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('eventType')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            {formData.eventType || t('selectEventType')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.eventType])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, eventType: Array.from(keys)[0] })}
                          >
                            {(eventTypes || EVENT_TYPES).map((et, i) => {
                              const etName = typeof et === 'string' ? et : et.name;
                              return (
                                <DropdownItem key={`${etName}-${i}`} id={etName}>
                                  {etName}
                                </DropdownItem>
                              );
                            })}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('venuePreference')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            {formData.venue ? ((venues && venues.length > 0 ? venues : VENUES).find(v => v.id === formData.venue)?.name || formData.venue) : t('selectVenue')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.venue])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, venue: Array.from(keys)[0] })}
                          >
                            {(venues && venues.length > 0 ? venues.filter(v => v.isLive !== false) : VENUES).map((v) => (
                              <DropdownItem key={v.id} id={v.id}>
                                {v.name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('source')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            {formData.source || t('unknown')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.source])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, source: Array.from(keys)[0] })}
                          >
                            {sourceOptions.map((s, i) => (
                              <DropdownItem key={s.id || `source-${i}`} id={s.name}>
                                {s.name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                  </div>
                </div>

                {/* Quotation Builder */}
                <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('qbTitle') || 'Quotation Builder'}</h3>
                    </div>
                    <span className="text-[11px] text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                      Guests: <span className="text-blue-600 font-bold">{quotationCalc.g}</span>
                    </span>
                  </div>

                  <div className="overflow-x-auto -mx-1 px-1 pb-2"><div className="min-w-[700px] sm:min-w-0">
                  {/* Column Labels */}
                  <div className="grid grid-cols-12 gap-x-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">
                    <div className="col-span-3">{t('qbService') || 'Service'}</div>
                    <div className="col-span-1 text-center">{t('qbStatus') || 'Status'}</div>
                    <div className="col-span-2 text-center">{t('qbAmtRate') || 'Amt / Rate'}</div>
                    <div className="col-span-1 text-center">{t('qbQty') || 'Qty'}</div>
                    <div className="col-span-2 text-center">{t('qbSubtotal') || 'Subtotal'}</div>
                    <div className="col-span-1 text-center">{t('qbDisc') || 'Disc'}</div>
                    <div className="col-span-2 text-right">{t('qbNet') || 'Net'}</div>
                  </div>

                  <div className="space-y-2">

                    {/* ── Lawn Rent ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbLawnRent') || 'Lawn Rent'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.lawnRent.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.lawnRent.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.lawnRent.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, lawnRent: { ...p.lawnRent, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.lrSub.toLocaleString()}</div>
                    </div>

                    {/* ── Decoration ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbDecoration') || 'Decoration'}</p>
                        <p className="text-[10px] text-blue-500 font-medium">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span>
                      </div>
                      <div className="col-span-2">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="0"
                          value={quotationItems.decoration.lumpsum}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, lumpsum: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-1 text-center text-xs text-gray-500">{quotationCalc.g}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">{(Number(quotationItems.decoration.lumpsum) || 0).toLocaleString()}</div>
                      <div className="col-span-1">
                        <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                          value={quotationItems.decoration.discount}
                          onChange={e => setQuotationItems(p => ({ ...p, decoration: { ...p.decoration, discount: e.target.value } }))}
                          disabled={isLockedStatus} />
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationCalc.decSub.toLocaleString()}</div>
                    </div>

                    {/* ── Cold Drink ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbColdDrink') || 'Cold Drink'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.coldDrink.enabled === 'yes'
                              ? (quotationItems.coldDrink.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.coldDrink.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.coldDrink.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            quotationItems.coldDrink.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.coldDrink.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.coldDrink.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.coldDrink.enabled === 'yes'
                            ? (quotationItems.coldDrink.inputMode === 1
                              ? (Number(quotationItems.coldDrink.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.coldDrink.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.coldDrink.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.coldDrink.enabled === 'yes' ? quotationCalc.cdSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.coldDrink.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, coldDrink: { ...p.coldDrink, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.coldDrink.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.coldDrink.inputMode === 1 && quotationCalc.cdPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.cdPerPax % 1 === 0 ? quotationCalc.cdPerPax.toLocaleString() : quotationCalc.cdPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.coldDrink.inputMode !== 1 && quotationItems.coldDrink.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.coldDrink.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Mineral Water ── */}
                    <div className="bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="grid grid-cols-12 gap-x-2 items-center">
                        <div className="col-span-3">
                          <p className="text-xs font-semibold text-gray-800">{t('qbMineralWater') || 'Mineral Water'}</p>
                          <p className="text-[10px] text-gray-400">
                            {quotationItems.mineralWater.enabled === 'yes'
                              ? (quotationItems.mineralWater.inputMode === 1 ? 'LumpSum' : 'Guests × Rate')
                              : 'Guests × Rate'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                                disabled={isLockedStatus}
                              >
                                {quotationItems.mineralWater.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.mineralWater.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                                <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set([quotationItems.mineralWater.enabled])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, enabled: Array.from(keys)[0] } }))}
                              >
                                <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                                <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                                <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div className="col-span-2">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            quotationItems.mineralWater.inputMode === 1 ? (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-purple-300 rounded text-xs text-center bg-white focus:border-purple-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Total amt"
                                value={quotationItems.mineralWater.lumpsum}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, lumpsum: e.target.value } }))}
                                disabled={isLockedStatus} />
                            ) : (
                              <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="Rate/pax"
                                value={quotationItems.mineralWater.rate}
                                onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, rate: e.target.value } }))}
                                disabled={isLockedStatus} />
                            )
                          ) : quotationItems.mineralWater.enabled === 'complementary' ? (
                            <span className="text-[10px] text-green-600 font-semibold text-center block">4 Dispensers</span>
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-1 text-center text-xs text-gray-500">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.g : '—'}</div>
                        <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                          {quotationItems.mineralWater.enabled === 'yes'
                            ? (quotationItems.mineralWater.inputMode === 1
                              ? (Number(quotationItems.mineralWater.lumpsum) || 0).toLocaleString()
                              : ((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString())
                            : '—'}
                        </div>
                        <div className="col-span-1">
                          {quotationItems.mineralWater.enabled === 'yes' ? (
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                              value={quotationItems.mineralWater.discount}
                              onChange={e => setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, discount: e.target.value } }))}
                              disabled={isLockedStatus} />
                          ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.mineralWater.enabled === 'yes' ? quotationCalc.mwSub.toLocaleString() : '—'}</div>
                      </div>
                      {quotationItems.mineralWater.enabled === 'yes' && (
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 2 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode !== 1 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Per Person
                            </button>
                            <button
                              type="button"
                              onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, mineralWater: { ...p.mineralWater, inputMode: 1 } }))}
                              disabled={isLockedStatus}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${quotationItems.mineralWater.inputMode === 1 ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              Lump Sum
                            </button>
                          </div>
                          {quotationItems.mineralWater.inputMode === 1 && quotationCalc.mwPerPax !== null && (
                            <span className="text-[10px] text-purple-600 font-semibold">
                              = PKR {quotationCalc.mwPerPax % 1 === 0 ? quotationCalc.mwPerPax.toLocaleString() : quotationCalc.mwPerPax.toFixed(2)} / person
                            </span>
                          )}
                          {quotationItems.mineralWater.inputMode !== 1 && quotationItems.mineralWater.rate && quotationCalc.g > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">
                              Total = PKR {((Number(quotationItems.mineralWater.rate) || 0) * quotationCalc.g).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Pantry Charges ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbPantryCharges') || 'Pantry Charges'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.pantryCharges.enabled === 'yes' ? (t('qbYes') || 'Yes') : (t('qbNo') || 'No')}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.pantryCharges.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.pantryCharges.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (Number(quotationItems.pantryCharges.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.pantryCharges.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.pantryCharges.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, pantryCharges: { ...p.pantryCharges, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.pantryCharges.enabled === 'yes' ? quotationCalc.pcSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* ── Flower Work ── */}
                    <div className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100">
                      <div className="col-span-3">
                        <p className="text-xs font-semibold text-gray-800">{t('qbFlowerWork') || 'Flower Work'}</p>
                        <p className="text-[10px] text-gray-400">{t('qbLumpSum') || 'LumpSum'}</p>
                      </div>
                      <div className="col-span-1">
                        <Dropdown>
                          <DropdownTrigger>
                            <button
                              type="button"
                              className="w-full p-1 border border-gray-200 rounded text-xs bg-white focus:border-blue-400 focus:outline-none text-left flex justify-between items-center"
                              disabled={isLockedStatus}
                            >
                              {quotationItems.freshFlower.enabled === 'yes' ? (t('qbYes') || 'Yes') : (quotationItems.freshFlower.enabled === 'complementary' ? (t('qbComplementary') || 'Complementary') : (t('qbNo') || 'No'))}
                              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                            <DropdownMenu
                              selectedKeys={new Set([quotationItems.freshFlower.enabled])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, enabled: Array.from(keys)[0] } }))}
                            >
                              <DropdownItem id="no">{t('qbNo') || 'No'}</DropdownItem>
                              <DropdownItem id="yes">{t('qbYes') || 'Yes'}</DropdownItem>
                              <DropdownItem id="complementary">{t('qbComplementary') || 'Complementary'}</DropdownItem>
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                      <div className="col-span-2">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center bg-white focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                            value={quotationItems.freshFlower.rate}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, rate: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : quotationItems.freshFlower.enabled === 'complementary' ? (
                          <span className="text-[10px] text-green-600 font-semibold text-center block">{t('qbOnStage') || 'On Stage'}</span>
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-1 text-center text-[10px] text-gray-400">{t('qbFixed') || 'Fixed'}</div>
                      <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                        {quotationItems.freshFlower.enabled === 'yes' ? (Number(quotationItems.freshFlower.rate) || 0).toLocaleString() : '—'}
                      </div>
                      <div className="col-span-1">
                        {quotationItems.freshFlower.enabled === 'yes' ? (
                          <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                            value={quotationItems.freshFlower.discount}
                            onChange={e => setQuotationItems(p => ({ ...p, freshFlower: { ...p.freshFlower, discount: e.target.value } }))}
                            disabled={isLockedStatus} />
                        ) : <span className="text-gray-300 text-xs text-center block">—</span>}
                      </div>
                      <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">{quotationItems.freshFlower.enabled === 'yes' ? quotationCalc.ffSub.toLocaleString() : '—'}</div>
                    </div>

                    {/* Addons */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-bold text-gray-600 uppercase">{t('qbAddons') || 'Addons'}</h4>
                            <button
                                type="button"
                                onClick={() => !isLockedStatus && setQuotationItems(p => ({ ...p, addons: [...(p.addons || []), { id: Date.now(), name: '', lumpsum: '', discount: '' }] }))}
                                disabled={isLockedStatus}
                                className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                            >
                                + Add More
                            </button>
                        </div>
                        
                        {(quotationItems.addons || []).map((addon, index) => (
                            <div key={addon.id || index} className="grid grid-cols-12 gap-x-2 items-center bg-slate-50 rounded-lg px-2 py-2 border border-gray-100 mt-2">
                                <div className="col-span-3">
                                    <input type="text" className="w-full p-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:outline-none" placeholder={t('qbAddonName') || 'Addon Name'}
                                        value={addon.name}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].name = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-1 flex justify-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-semibold">{t('qbFixed') || 'Fixed'}</span></div>
                                <div className="col-span-2">
                                    <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-gray-200 rounded text-xs text-center focus:border-blue-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder={t('qbAmount') || 'Amount'}
                                        value={addon.lumpsum}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].lumpsum = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-2 text-center text-xs text-gray-700 font-medium whitespace-nowrap">
                                    {(Number(addon.lumpsum) || 0).toLocaleString()}
                                </div>
                                <div className="col-span-1">
                                    <input type="number" onWheel={(e) => e.target.blur()} min="0" className="w-full min-w-0 p-1 border border-red-200 bg-red-50 rounded text-xs text-center focus:border-red-400 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder=""
                                        value={addon.discount}
                                        onChange={e => {
                                            const newAddons = [...quotationItems.addons];
                                            newAddons[index].discount = e.target.value;
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus} />
                                </div>
                                <div className="col-span-2 text-right text-xs font-bold text-emerald-700 whitespace-nowrap">
                                    {Math.max(0, (Number(addon.lumpsum) || 0) - (Number(addon.discount) || 0)).toLocaleString()}
                                </div>
                                <div className="col-span-1 text-right">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if(isLockedStatus) return;
                                            const newAddons = quotationItems.addons.filter((_, i) => i !== index);
                                            setQuotationItems(p => ({ ...p, addons: newAddons }));
                                        }}
                                        disabled={isLockedStatus}
                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Grand Total */}
                    <div className="flex items-center justify-between bg-blue-600 text-white rounded-lg px-4 py-3 mt-1">
                      <span className="text-xs font-bold uppercase tracking-widest">{t('qbGrandTotal') || 'Grand Total'}</span>
                      <span className="text-base font-bold">PKR {quotationCalc.total.toLocaleString()}</span>
                    </div>
                </div>
              </div>

                  </div>
                </div>



                {/* Site Visit Section */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">
                    {t('siteVisit')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('siteVisitDate')}</label>
                      <DatePicker
                        className={`mt-1 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.siteVisitDate ? String(formData.siteVisitDate).split('T')[0] : ''}
                        onChange={(e) =>
                          setFormData({ ...formData, siteVisitDate: e.target.value })
                        }
                        disabled={isLockedStatus}
                        placement="top"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('siteVisitTime')}</label>
                      <input
                        type="time"
                        className={`w-full p-2 border rounded-lg text-sm mt-1 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.siteVisitTime ? String(formData.siteVisitTime).substring(0, 5) : ''}
                        onChange={(e) =>
                          setFormData({ ...formData, siteVisitTime: e.target.value })
                        }
                        disabled={isLockedStatus}
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-2 flex items-center mt-2">
                      <CustomCheckbox
                        name="siteVisited"
                        isSelected={formData.siteVisited || false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData({ ...formData, siteVisited: isChecked });
                          if (isChecked && formData.stage !== 'Site Visited' && formData.stage !== 'Won' && formData.stage !== 'Lost') {
                            handleStageClick('Site Visited');
                          }
                        }}
                        isDisabled={isLockedStatus}
                      >
                        {t('markSiteVisited') || 'Mark as Site Visited'}
                      </CustomCheckbox>
                    </div>
                  </div>
                </div>

                {/* Calendar Actions */}
                {(formData.eventDate || formData.siteVisitDate || formData.nextCallDate) && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <CalendarPlus className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">
                        {t('addToGoogleCalendar')}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.siteVisitDate && (
                        <div className="inline-flex items-center bg-white border border-blue-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateSiteVisitCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-blue-700 text-sm font-medium hover:bg-blue-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <MapPin className="w-4 h-4" />
                            {t('siteVisit')}
                            <span className="text-xs text-blue-500">
                              ({formatDate(formData.siteVisitDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('siteVisit'); }}
                              className="px-2 py-2 text-blue-400 hover:text-red-500 hover:bg-red-50 border-l border-blue-200 transition-colors"
                              title="Remove Site Visit date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {formData.nextCallDate && (
                        <div className="inline-flex items-center bg-white border border-green-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateCallCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-green-700 text-sm font-medium hover:bg-green-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <Phone className="w-4 h-4" />
                            {t('scheduledCall')}
                            <span className="text-xs text-green-500">
                              ({formatDate(formData.nextCallDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('call'); }}
                              className="px-2 py-2 text-green-400 hover:text-red-500 hover:bg-red-50 border-l border-green-200 transition-colors"
                              title="Remove Scheduled Call date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {formData.eventDate && (
                        <div className="inline-flex items-center bg-white border border-purple-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateEventCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <Calendar className="w-4 h-4" />
                            {formData.stage === 'Won' ? t('booked') : t('eventDate')}
                            <span className="text-xs text-purple-500">
                              ({formatDate(formData.eventDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('event'); }}
                              className="px-2 py-2 text-purple-400 hover:text-red-500 hover:bg-red-50 border-l border-purple-200 transition-colors"
                              title="Remove Event Date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-500 mt-3">
                      {t('googleCalendarHelperText')}
                    </p>

                    {/* Calendar Delete Confirmation Modal */}
                    {calendarDeleteConfirm && (
                      <div className="mt-4 bg-white border border-red-200 rounded-xl p-4 shadow-sm animate-in fade-in duration-200">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-red-50 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800 mb-1">
                              Remove {calendarDeleteLabels[calendarDeleteConfirm]?.label}?
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                              This will clear the date ({formatDate(calendarDeleteLabels[calendarDeleteConfirm]?.date)}) and remove it from the call list. You will need to click Save to apply the changes.
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCalendarDeleteConfirm(null)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleCalendarEntryDelete}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
                    {t('notesSection')}
                  </h3>
                  <textarea
                    className={`w-full p-3 border rounded-lg text-sm h-32 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={isLockedStatus}
                  />
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="flex flex-col h-full">
                {/* Timeline */}
                <div className="flex-1 space-y-4 mb-4 overflow-y-auto">
                  {timelineLoading ? (
                    [1, 2, 3].map(i => (
                      <div key={`sk-${i}`} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Skeleton variant="circular" width={20} height={20} animation="wave" />
                        </div>
                        <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                          <Skeleton width="60%" height={16} animation="wave" className="mb-2" />
                          <Skeleton width="40%" height={12} animation="wave" />
                        </div>
                      </div>
                    ))
                  ) : timeline.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('noActivityYet')}</p>
                      <p className="text-xs mt-1">{t('addNoteToTrack')}</p>
                    </div>
                  ) : (
                    timeline.map((item, i) => {
                      // All items from API are activities with proper types
                      const meta = getActivityMeta(item.type);
                      return (
                        <div key={i} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                            <span className="text-sm">{meta.icon}</span>
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                            <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                              {formatActivityMessage({ type: item.type, ...item.data })}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {item.user} • {item.timestamp.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={activityEndRef} />
                </div>

                {/* Add Note Input */}
                <div className="flex gap-2 pt-4 border-t">
                  <input
                    className={`flex-1 p-3 rounded-xl border outline-none text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('addNotePlaceholder')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    disabled={isLockedStatus}
                  />
                  <button
                    onClick={handleAddComment}
                    className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 bg-white border-t border-gray-200 flex justify-end gap-2 relative z-20 shrink-0">
          <button
            onClick={handleSave}
            disabled={isSaving || isLockedStatus || isPastEventDate}
            className={`px-5 py-2.5 text-sm text-white font-semibold rounded-xl flex items-center gap-2 transition-all ${isSaving || isLockedStatus || isPastEventDate ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
              }`}
          >
            {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? (t('saving') || 'Saving...') : t('save')}
          </button>
        </div>
      </div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
